import {
  Type, Hash, DollarSign, ListChecks, Calendar, Image as ImageIcon, Braces, CheckSquare, AlignLeft, Link2, Tags, Paperclip, Star, ListOrdered, FileText, PenLine,
} from 'lucide-react';
import { DEFAULT_THEME } from './theme';

// `common: true` (docs/progressive-disclosure-plan.md §5.3) marks the six
// types that cover the overwhelming majority of real fields - the builder's
// type pickers (BuilderCanvas's AddFieldButton, FieldRow's type <select>)
// show these first and put the rest behind a "More types" disclosure. This
// is the one place that decides the split; both render sites read the flag
// rather than hand-listing which types are common.
export const FIELD_TYPES = [
  { id: 'string', label: 'Text', icon: Type, common: true },
  { id: 'number', label: 'Number', icon: Hash, common: true },
  { id: 'money', label: 'Money', icon: DollarSign },
  { id: 'enum', label: 'Choice', icon: ListChecks, common: true },
  { id: 'multiselect', label: 'Multi-select', icon: Tags },
  { id: 'reference', label: 'Reference', icon: Link2, common: true },
  { id: 'datetime', label: 'Date', icon: Calendar, common: true },
  { id: 'image', label: 'Image URL', icon: ImageIcon },
  { id: 'file', label: 'File', icon: Paperclip },
  { id: 'json', label: 'Group', icon: Braces },
  { id: 'boolean', label: 'Checkbox', icon: CheckSquare, common: true },
  { id: 'longtext', label: 'Long text', icon: AlignLeft },
  { id: 'richtext', label: 'Formatted text', icon: FileText },
  { id: 'rating', label: 'Rating', icon: Star },
  { id: 'sequence', label: 'Auto-number', icon: ListOrdered },
  { id: 'signature', label: 'Signature', icon: PenLine },
];

// The numeric-ish field types, in one place because a dozen surfaces need
// "is this field a number I can sum/chart/filter/edit-inline?" and, before
// this existed, each hand-listed `['number']` — so `money` (added later) was
// silently absent from the older ones while the newer `timeseries`/`scatter`
// panels included it. Several of those sites already carried a `money` branch
// that could never run: TableView's numeric <input>, FilterBar's numeric
// operand box. Read this rather than re-listing, so the next numeric-ish type
// doesn't re-scatter the same way. Mirrors the pairing `db/schema.py`'s
// `_EXPECTED_UDT` already makes on the backend ({"numeric"} for both).
export const NUMERIC_TYPES = ['number', 'money'];

export const SUB_TYPES = [
  { id: 'string', label: 'Text' },
  { id: 'number', label: 'Number' },
  { id: 'datetime', label: 'Date' },
];

// Plain-English on purpose, and worded for what these actually do: every
// delete in this product is a soft delete (a trashed row is fully restorable,
// and every reference to it survives untouched — see the platform's own
// soft-delete invariant), so `on_delete` never fires on an everyday delete.
// It only fires when a trashed row is permanently purged — the labels say so
// rather than using database jargon ("Cascade"/"Restrict") that would read
// as if it applied to every delete.
export const ON_DELETE_OPTIONS = [
  { id: 'set_null', label: 'Clear this field' },
  { id: 'cascade', label: 'Delete this record too' },
  { id: 'restrict', label: 'Block the delete' },
];

// Field-level encryption (`sensitive: true`) — mirrors
// backend/appdata/sensitive_rules.py's `_ALLOWED_SENSITIVE_TYPES` exactly;
// keep these two in sync if that set ever changes. Not every type shown in
// FIELD_TYPES can be marked sensitive (a computed/virtual field never can
// either — checked separately, since it's about `compute` not `type`).
export const SENSITIVE_TYPES = ['string', 'longtext', 'json'];

// `sensitive_kind` — a pure format/display hint on a `sensitive` field, never
// a way to skip encryption. Mirrors `_ALLOWED_SENSITIVE_KINDS`; `stringOnly`
// mirrors `_STRING_ONLY_SENSITIVE_KINDS` (everything but `generic`, which is
// also the only kind valid on longtext/json).
export const SENSITIVE_KINDS = [
  { id: 'generic', label: 'Generic', stringOnly: false },
  { id: 'email', label: 'Email', stringOnly: true },
  { id: 'phone', label: 'Phone number', stringOnly: true },
  { id: 'credit_card', label: 'Credit card number', stringOnly: true },
];

// The three flavours of computed field offered in the builder.
export const COMPUTE_KINDS = [
  { id: 'formula', label: 'Formula', hint: 'An expression over this row’s own fields.' },
  { id: 'rollup', label: 'Rollup', hint: 'Aggregate a related (child) entity.' },
  { id: 'working_days', label: 'Working days', hint: 'Business days between two dates.' },
  { id: 'business_hours', label: 'Business hours', hint: 'Elapsed business hours between two datetimes.' },
];

export const ROLLUP_FNS = ['count', 'sum', 'avg', 'min', 'max'];

// Reference shown in the formula editor. Display-only; the authoritative set of
// functions lives in the backend (engine._FUNCS) and is enforced there.
export const FORMULA_FUNCTIONS = [
  { sig: 'IF(cond, then, else)', desc: 'Branch on a condition' },
  { sig: 'COALESCE(a, b, …)', desc: 'First non-empty value' },
  { sig: 'ROUND(x, dp)', desc: 'Round to dp decimals' },
  { sig: 'ABS / FLOOR / CEIL / INT(x)', desc: 'Numeric helpers' },
  { sig: 'MIN / MAX(a, b, …)', desc: 'Smallest / largest' },
  { sig: 'CONCAT(a, b, …)', desc: 'Join as text' },
  { sig: 'UPPER / LOWER / TRIM(s)', desc: 'Text case / whitespace' },
  { sig: 'LEN(s)', desc: 'Text length' },
  { sig: 'CONTAINS / STARTSWITH / ENDSWITH(s, x)', desc: 'Text tests → true/false' },
  { sig: 'IN(x, a, b, …)', desc: 'Is x one of these?' },
  { sig: 'ISBLANK(x)', desc: 'Is empty/null?' },
  { sig: 'NULLIF(a, b)', desc: 'Null when a equals b' },
  { sig: 'DAYS_BETWEEN(start, end)', desc: 'Calendar days' },
  { sig: 'HOURS_BETWEEN(start, end)', desc: 'Elapsed hours (fractional)' },
  { sig: 'YEAR(date)  MONTH(date)  DAY(date)', desc: 'Date-part extraction' },
  { sig: 'TODAY()  NOW()', desc: 'Current date / timestamp' },
  { sig: "LOOKUP(entity, field, value, return)", desc: 'Exact-match lookup in another entity' },
  { sig: "LOOKUP_RANGE(entity, return, start_f, end_f, value)", desc: 'Find row where start_f <= value <= end_f' },
  { sig: "LOOKUP_WHERE(entity, return, f, op, v, …)", desc: 'Multi-condition lookup (ops: eq ne gt gte lt lte contains)' },
  { sig: 'NOT(x)  AND(…)  OR(…)', desc: 'Logical (function form)' },
  { sig: 'ref__field', desc: 'Value from a referenced record' },
  { sig: '+  -  *  /  %', desc: 'Arithmetic (+ also joins text)' },
  { sig: '>  >=  <  <=  ==  !=', desc: 'Comparisons → true/false' },
  { sig: 'and  or  not', desc: 'Combine conditions' },
  { sig: 'true  false  null', desc: 'Literals' },
];

// The table is named after the APP, not fixed: a table name is unique per org
// (the save path refuses a second app claiming one another app owns), so a
// hard-coded 'records' made every blank app after an org's first fail to
// create with "Entity 'record' claims table 'records', which is owned by app
// …". `appId` is already a unique slug (App.jsx's uniqueKey over the org's
// apps); 'records' remains only for a caller that has no id to pass.
const blankTable = (appId) => {
  const base = String(appId || '').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
  return base ? `${base.slice(0, 50)}_records` : 'records';
};

// The one list is named after the app ("Cake orders"), not "Records": every
// screen it reaches — the form title, the table count, the builder rail —
// read "New Record" / "1 Record" otherwise, words nobody chose.
export const blankApp = (name, appId) => ({
  name, description: '', theme: { ...DEFAULT_THEME },
  entities: {
    record: {
      label: (name || '').trim() || 'Records', table: blankTable(appId), primary_key: 'id',
      fields: {
        id: { type: 'uuid', label: 'ID', readonly: true, hide_in_form: true },
        name: { type: 'string', label: 'Name', required: true },
        created_at: { type: 'datetime', label: 'Created', readonly: true, hide_in_form: true },
      },
      list_columns: ['name'], default_order: { field: 'created_at', dir: 'desc' },
      field_order: ['id', 'name', 'created_at'],
      ops: ['list', 'get', 'create', 'update', 'delete'], actions: [], sublists: [],
    },
  },
  views: [{ type: 'table', entity: 'record', title: (name || '').trim() ? `All ${name.trim().toLowerCase()}` : 'All records', filters: [], search: ['name'] }],
  roles: { admin: { label: 'Admin', entities: { '*': { ops: '*', scope: 'all' } } } },
});

// Onboarding gallery shown on "New app": the code-level fallback plus
// whatever `kesher-templates/` has (fetched from `GET /api/templates` by
// `TemplateGalleryModal.jsx`). Before Sprint 4
// (docs/recipes-templates-domains-plan-2026-07.md, F8a) this array also held
// four hand-written JS builders (CRM/helpdesk/project/inventory) that shared
// nothing with the 23-and-growing JSON template directory the config-builder
// agent already reads — two systems, no overlap. `blank` is the one entry
// that has to stay code-level: it's the escape hatch a template fetch
// failure falls back to, so it can't itself depend on that fetch succeeding.
export const STARTER_TEMPLATES = [
  { id: 'blank', label: 'Blank', description: 'One empty list you add your own fields to', icon: 'FilePlus', build: blankApp },
];
